import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, Box, Typography, Button, TextField, CircularProgress, LinearProgress } from '@mui/material';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FolderIcon from '@mui/icons-material/Folder';
import StorageIcon from '@mui/icons-material/Storage';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';

interface CreateWorkspaceModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (workspaceId: string) => void;
  onCreateWorkspace: (name: string) => Promise<{ success: boolean; workspace?: { id: string; name: string } }>;
}

const POLL_INTERVAL = 3000;
const MAX_POLL_TIME = 60000;

const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({
  open,
  onClose,
  onComplete,
  onCreateWorkspace,
}) => {
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();

  const [currentStep, setCurrentStep] = useState(0);
  const [workspaceName, setWorkspaceName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [provisionProgress, setProvisionProgress] = useState(0);
  const [error, setError] = useState('');
  const [createdWorkspace, setCreatedWorkspace] = useState<{ id: string; name: string } | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number>(0);

  // Poll for workspace readiness
  const startPolling = useCallback((wsId: string) => {
    setIsProvisioning(true);
    setProvisionProgress(0);
    pollStartRef.current = Date.now();

    // Smooth progress animation (reaches ~90% over 30s)
    progressTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - pollStartRef.current;
      const pct = Math.min(90, (elapsed / 30000) * 90);
      setProvisionProgress(pct);
    }, 500);

    // Poll workspace list
    pollTimerRef.current = setInterval(async () => {
      try {
        const resp = await withTokenRefresh(() => apiClient.getWorkspaces());
        if (resp?.success && resp?.workspaces) {
          const found = (resp.workspaces as any[]).some((w: any) => w.id === wsId);
          if (found) {
            stopPolling();
            setProvisionProgress(100);
            setTimeout(() => {
              setIsProvisioning(false);
              setCurrentStep(2);
            }, 600);
            return;
          }
        }
      } catch { /* ignore */ }

      // Timeout fallback
      if (Date.now() - pollStartRef.current > MAX_POLL_TIME) {
        stopPolling();
        setProvisionProgress(100);
        setIsProvisioning(false);
        setCurrentStep(2);
      }
    }, POLL_INTERVAL);
  }, [apiClient, withTokenRefresh]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if (progressTimerRef.current) { clearInterval(progressTimerRef.current); progressTimerRef.current = null; }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateWorkspace = async () => {
    if (!workspaceName.trim()) {
      setError('Please enter a workspace name');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const result = await onCreateWorkspace(workspaceName.trim());
      setIsCreating(false);
      if (result.success && result.workspace) {
        setCreatedWorkspace(result.workspace);
        // Start polling for workspace readiness instead of jumping to success
        startPolling(result.workspace.id);
      } else {
        setError('Failed to create workspace. Please try again.');
      }
    } catch (err) {
      setIsCreating(false);
      setError('An error occurred. Please try again.');
    }
  };

  const handleFinish = () => {
    if (createdWorkspace) {
      onComplete(createdWorkspace.id);
    }
    // Reset state
    setCurrentStep(0);
    setWorkspaceName('');
    setCreatedWorkspace(null);
    setError('');
  };

  const handleCloseModal = () => {
    // Reset state on close
    stopPolling();
    setCurrentStep(0);
    setWorkspaceName('');
    setCreatedWorkspace(null);
    setError('');
    setIsProvisioning(false);
    setProvisionProgress(0);
    onClose();
  };

  if (!open) return null;

  const steps = [
    // Step 0: Introduction
    {
      title: 'Create Your Workspace',
      content: (
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 12px 40px rgba(79, 172, 254, 0.4)',
              }}
            >
              <WorkspacesIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>
          </Box>
          
          <Typography sx={{ fontSize: '16px', color: '#535862', lineHeight: 1.7, mb: 4, maxWidth: 420, mx: 'auto' }}>
            A <strong>Workspace</strong> is your organizational container that groups all your cloud resources together.
          </Typography>

          {/* Feature cards like CloudGatewayIntroModal */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: '14px', bgcolor: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5, boxShadow: '0 2px 8px rgba(79, 172, 254, 0.15)' }}>
                <StorageIcon sx={{ color: '#4facfe', fontSize: 28 }} />
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Buckets</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: '14px', bgcolor: '#FDF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5, boxShadow: '0 2px 8px rgba(168, 85, 247, 0.15)' }}>
                <SmartToyIcon sx={{ color: '#A855F7', fontSize: 28 }} />
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>AI Models</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: '14px', bgcolor: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5, boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)' }}>
                <FolderIcon sx={{ color: '#10B981', fontSize: 28 }} />
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Repos</Typography>
            </Box>
          </Box>
        </Box>
      ),
    },
    // Step 1: Create Workspace Form (or provisioning state)
    {
      title: isProvisioning ? 'Setting Up Your Workspace...' : 'Name Your Workspace',
      content: isProvisioning ? (
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 12px 40px rgba(79, 172, 254, 0.4)',
                animation: 'pulse-workspace 2s ease-in-out infinite',
                '@keyframes pulse-workspace': {
                  '0%': { boxShadow: '0 12px 40px rgba(79, 172, 254, 0.4)' },
                  '50%': { boxShadow: '0 12px 60px rgba(79, 172, 254, 0.6)' },
                  '100%': { boxShadow: '0 12px 40px rgba(79, 172, 254, 0.4)' },
                },
              }}
            >
              <WorkspacesIcon sx={{ fontSize: 50, color: 'white' }} />
            </Box>
          </Box>

          <Typography sx={{ fontSize: '16px', color: '#535862', lineHeight: 1.7, mb: 1, maxWidth: 380, mx: 'auto' }}>
            Creating <strong style={{ color: '#4facfe' }}>"{createdWorkspace?.name}"</strong>
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#9CA3AF', mb: 3 }}>
            Provisioning resources, this may take a moment...
          </Typography>

          <Box sx={{ maxWidth: 320, mx: 'auto' }}>
            <LinearProgress
              variant="determinate"
              value={provisionProgress}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: '#E5E7EB',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)',
                  transition: 'transform 0.5s ease',
                },
              }}
            />
            <Typography sx={{ fontSize: '12px', color: '#9CA3AF', mt: 1, textAlign: 'right' }}>
              {Math.round(provisionProgress)}%
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(79, 172, 254, 0.3)',
              }}
            >
              <WorkspacesIcon sx={{ fontSize: 36, color: 'white' }} />
            </Box>
          </Box>

          <Typography sx={{ fontSize: '15px', color: '#535862', lineHeight: 1.7, mb: 4, maxWidth: 380, mx: 'auto' }}>
            Choose a name for your workspace. You can rename it later or create more workspaces.
          </Typography>

          <Box sx={{ maxWidth: 360, mx: 'auto', textAlign: 'left' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151', mb: 1 }}>
              Workspace Name
            </Typography>
            <TextField
              fullWidth
              value={workspaceName}
              onChange={(e) => {
                setWorkspaceName(e.target.value);
                setError('');
              }}
              placeholder="e.g., My Project, Development, Production..."
              error={!!error}
              helperText={error}
              disabled={isCreating}
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: 'white',
                  fontSize: '15px',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#4facfe' },
                  '&.Mui-focused fieldset': { borderColor: '#4facfe', borderWidth: '2px' },
                },
                '& .MuiOutlinedInput-input': {
                  py: 1.5,
                },
              }}
            />
            <Typography sx={{ fontSize: '12px', color: '#9CA3AF', mt: 1.5 }}>
              Examples: "Production", "Development", "Personal"
            </Typography>
          </Box>
        </Box>
      ),
    },
    // Step 2: Success
    {
      title: 'Workspace Created!',
      content: (
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4)',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 56, color: 'white' }} />
            </Box>
          </Box>

          <Typography sx={{ fontSize: '16px', color: '#535862', lineHeight: 1.7, mb: 4, maxWidth: 380, mx: 'auto' }}>
            Your workspace <strong style={{ color: '#10B981' }}>"{createdWorkspace?.name}"</strong> has been created successfully!
          </Typography>

          <Box sx={{ bgcolor: '#F0FDF4', borderRadius: '16px', p: 3, border: '1px solid #D1FAE5' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#059669', mb: 2.5 }}>
              What's next?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}>
              {[
                { icon: <StorageIcon sx={{ fontSize: 18, color: '#10B981' }} />, text: 'Add your first cloud storage bucket' },
                { icon: <SmartToyIcon sx={{ fontSize: 18, color: '#10B981' }} />, text: 'Configure an AI provider' },
                { icon: <FolderIcon sx={{ fontSize: 18, color: '#10B981' }} />, text: 'Create a repository with API access' },
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>{item.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      ),
    },
  ];

  const currentStepData = steps[currentStep];

  return (
    <Dialog
      open={open}
      onClose={currentStep === 2 ? handleFinish : handleCloseModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxWidth: '520px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Progress bar */}
        <Box sx={{ display: 'flex', gap: 0.5, p: 2, pb: 0 }}>
          {steps.map((_, idx) => (
            <Box
              key={idx}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                bgcolor: idx <= currentStep ? '#4facfe' : '#E5E7EB',
                transition: 'background-color 0.3s ease',
              }}
            />
          ))}
        </Box>

        {/* Content */}
        <Box sx={{ p: 4, pt: 3 }}>
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#181D27',
              mb: 3,
              textAlign: 'center',
            }}
          >
            {currentStepData.title}
          </Typography>

          {currentStepData.content}
        </Box>

        {/* Navigation */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: currentStep === 0 ? 'flex-end' : 'space-between',
            alignItems: 'center',
            p: 3,
            pt: 0,
            gap: 2,
          }}
        >
          {currentStep > 0 && currentStep < 2 && !isProvisioning && (
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={isCreating}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                borderColor: '#D5D7DA',
                color: '#535862',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  borderColor: '#4facfe',
                  bgcolor: 'rgba(79, 172, 254, 0.04)',
                },
              }}
            >
              Back
            </Button>
          )}

          {currentStep === 0 && (
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                bgcolor: '#4facfe',
                fontWeight: 600,
                px: 4,
                boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)',
                '&:hover': {
                  bgcolor: '#3d9be8',
                },
              }}
            >
              Get Started
            </Button>
          )}

          {currentStep === 1 && !isProvisioning && (
            <Button
              variant="contained"
              onClick={handleCreateWorkspace}
              disabled={isCreating || !workspaceName.trim()}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                bgcolor: '#4facfe',
                fontWeight: 600,
                px: 4,
                minWidth: 160,
                boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)',
                '&:hover': {
                  bgcolor: '#3d9be8',
                },
                '&.Mui-disabled': {
                  bgcolor: '#E5E7EB',
                  color: '#9CA3AF',
                },
              }}
            >
              {isCreating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Create Workspace'
              )}
            </Button>
          )}

          {currentStep === 2 && (
            <Button
              variant="contained"
              onClick={handleFinish}
              fullWidth
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                bgcolor: '#10B981',
                fontWeight: 600,
                py: 1.5,
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                '&:hover': {
                  bgcolor: '#059669',
                },
              }}
            >
              Go to My Workspace
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreateWorkspaceModal;
